// src/components/DashboardPage.jsx
import BASE_URL from '../config';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from "./Header";


import './LandingPage.css';
import './DashboardPage.css';


// Recharts
import {
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  Legend,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

/* --------------------------
   Canonicalization
--------------------------- */
// Map any variant logged by UploadPage to the display key used in charts
const CANONICALS = {
  // Naive
  xceptionnet: 'Xception',
  xception: 'Xception',
  efficientnetb4: 'Efficient',
  efficient: 'Efficient',
  vitb16: 'ViT-B/16',
  'vit-b16': 'ViT-B/16',
  'vit-b/16': 'ViT-B/16',

  // Frequency
  f3net: 'F3Net',
  spsl: 'SPSL',
  srm: 'SRM',

  // Fairness
  'dag-fdd': 'DAG-FDD',
  dagfdd: 'DAG-FDD',
  'daw-fdd': 'DAW-FDD',
  dawfdd: 'DAW-FDD',
  pgfdd: 'PGFDD',

  // Spatial
  ucf: 'UCF',
  core: 'CORE',
  univfd: 'UnivFD',

  // Audio
  audiofakedetector: 'AUDIO',
  audio: 'AUDIO',
  audio_model: 'AUDIO',
};

// normalize like UploadPage logging / backend keys
const norm = (s) => String(s || '')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, ''); // strip separators

const toCanonical = (s) => CANONICALS[norm(s)] || s;

/* --------------------------
   Model categories (display)
--------------------------- */
const MODEL_CATEGORIES = {
  Naive: ['Xception', 'Efficient', 'ViT-B/16'],
  Frequency: ['F3Net', 'SPSL', 'SRM'],
  'Fairness Enhanced': ['DAG-FDD', 'DAW-FDD', 'PGFDD'],
  Spatial: ['UCF', 'CORE', 'UnivFD'],
  Audio: ['AUDIO'],
};
const ALL_MODELS = Object.values(MODEL_CATEGORIES).flat();
const ALL_CATEGORIES = Object.keys(MODEL_CATEGORIES);

/* --------------------------
   Helpers
--------------------------- */
const safeInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Extract model counts from API, folding aliases to canonical display keys
function extractModelCounts(raw) {
  const out = Object.fromEntries(ALL_MODELS.map((m) => [m, 0]));

  // Preferred shape: { model_counts: { "XceptionNet": 12, "EfficientNetB4": 7, ... } }
  if (raw && raw.model_counts && typeof raw.model_counts === 'object' && !Array.isArray(raw.model_counts)) {
    for (const [k, v] of Object.entries(raw.model_counts)) {
      const key = toCanonical(k);
      if (out[key] !== undefined) out[key] += safeInt(v);
    }
    return out;
  }

  // Fallback: flattened snake-case keys like xceptionnet_count, vitb16_count, etc.
  // 1) scan aliases we know about
  for (const [alias, canonical] of Object.entries(CANONICALS)) {
    const key1 = `${alias}_count`;
    if (raw && Object.prototype.hasOwnProperty.call(raw, key1)) {
      out[canonical] += safeInt(raw[key1]);
    }
  }
  // 2) scan canonical labels themselves (e.g., "xception_count")
  for (const m of ALL_MODELS) {
    const k = `${norm(m)}_count`;
    if (raw && Object.prototype.hasOwnProperty.call(raw, k)) {
      out[m] += safeInt(raw[k]);
    }
  }
  return out;
}

function sumCategoryCounts(modelCounts) {
  const byCat = {};
  for (const cat of ALL_CATEGORIES) {
    byCat[cat] = MODEL_CATEGORIES[cat].reduce((acc, m) => acc + (modelCounts[m] || 0), 0);
  }
  return byCat;
}

/* --------------------------
   UI bits (no libraries)
--------------------------- */
function KpiCard({ title, value, children }) {
  return (
    <div className="kp-card" tabIndex={0}>
      <div className="kp-topline" />
      <div className="kp-body">
        <div className="kp-title">{title}</div>
        <div className="kp-value">{value}</div>
        {children}
      </div>
    </div>
  );
}

function ChartCard({ title, height = 280, children }) {
  return (
    <div className="chart-card">
      <div className="kp-topline" />
      <div className="chart-header"><h4>{title}</h4></div>
      <div style={{ width: '100%', height }}>{children}</div>
    </div>
  );
}

/* --------------------------
   Charts
--------------------------- */
function PieAllModels({ modelCounts }) {
  const data = useMemo(
    () =>
      Object.entries(modelCounts)
        .map(([name, value]) => ({ name, value }))
        .filter((d) => d.value > 0),
    [modelCounts]
  );

  const colors = ['#00D084', '#00B0FF', '#8E2DE2', '#20C997', '#6C63FF', '#FF7A59', '#02C39A', '#4CC9F0'];

  return (
    <ChartCard title="All Models Used (Pie)">
      <ResponsiveContainer>
        <PieChart>
          <Tooltip />
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={2}
          >
            {data.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
          </Pie>
          <Legend verticalAlign="bottom" height={24} />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function BarByCategory({ modelCounts }) {
  const byCat = useMemo(() => {
    const sums = sumCategoryCounts(modelCounts);
    return Object.entries(sums).map(([category, count]) => ({ category, count }));
  }, [modelCounts]);

  return (
    <ChartCard title="Predictions by Category (Bar)">
      <ResponsiveContainer>
        <BarChart data={byCat} margin={{ left: 8, right: 8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="category" />
          <YAxis />
          <Tooltip />
          <defs>
            <linearGradient id="sgBar" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#00B0FF" />
              <stop offset="100%" stopColor="#00D084" />
            </linearGradient>
          </defs>
          <Bar dataKey="count" fill="url(#sgBar)" radius={[8, 8, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function LineActivity({ timeline }) {
  return (
    <ChartCard title="Activity Over Time (Line)">
      <ResponsiveContainer>
        <LineChart data={timeline} margin={{ left: 8, right: 8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Line type="monotone" dataKey="count" stroke="#00B0FF" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

/* NEW: Users by Region */
function RegionCard({ data }) {
  const base = [
    { region: 'Americas', count: 0 },
    { region: 'Europe',   count: 0 },
    { region: 'Asia',     count: 0 },
    { region: 'Africa',   count: 0 },
    { region: 'Oceania',  count: 0 },
  ];
  const merged = base.map((b) => {
    const found = data.find(
      (d) => (d.region || d.name || '').toLowerCase() === b.region.toLowerCase()
    );
    return { ...b, count: safeInt(found?.count ?? found?.value) };
  });

  const cols = ['#00D084', '#00B0FF', '#8E2DE2', '#6C63FF', '#20C997'];

  return (
    <ChartCard title="Users by Region">
      <ResponsiveContainer>
        <BarChart data={merged} margin={{ left: 8, right: 8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="region" />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Bar dataKey="count" radius={[8, 8, 0, 0]}>
            {merged.map((_, i) => <Cell key={i} fill={cols[i % cols.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

/* --------------------------
   Page
--------------------------- */
export default function DashboardPage() {
  const navigate = useNavigate();
  const [activeLink, setActiveLink] = useState('Statistics');

  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({
    totalUsers: 0,
    totalPredictions: 0,
    images: 0,
    audio: 0,
    real: 0,
    fake: 0,
  });
  const [modelCounts, setModelCounts] = useState({});
  const [timeline, setTimeline] = useState([]);
  const [regions, setRegions] = useState([]);

  const menuItems = ['Home', 'Newsroom', 'Interviews', 'Statistics', 'Team', 'Feedback'];

  const handleNavClick = (item) => {
    setActiveLink(item);
    if (item === 'Home') navigate('/');
    else if (item === 'Team') navigate('/team');
    else if (item === 'Feedback') navigate('/feedback');
    else if (item === 'Statistics') navigate('/statistics');
    else if (item === 'Interviews') navigate('/interview');
    else navigate(`/${item.toLowerCase()}`);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/dashboard-metrics`, { credentials: 'include' });
        const raw = await res.json();

        const totalUsers        = safeInt(raw.total_users ?? raw.totalUsers);
        const totalPredictions  = safeInt(raw.total_predictions ?? raw.totalPredictions);
        const real              = safeInt(raw.real_count ?? raw.real);
        const fake              = safeInt(raw.fake_count ?? raw.fake);
        const images            = safeInt(raw.images ?? raw.image_count ?? raw.images_count ?? raw.media_images);
        const audio             = safeInt(raw.audio ?? raw.audio_count ?? raw.media_audio);

        const mc                = extractModelCounts(raw);
        const ts                = Array.isArray(raw.timeline)
                                  ? raw.timeline
                                  : Array.isArray(raw.usage_over_time)
                                    ? raw.usage_over_time
                                    : [];

        // Regions can arrive as array or object; normalize to [{region, count}]
        let reg = [];
        if (Array.isArray(raw.regions)) {
          reg = raw.regions.map((r) => ({
            region: r.region || r.name,
            count:  safeInt(r.count ?? r.value),
          }));
        } else if (raw.region_counts && typeof raw.region_counts === 'object') {
          reg = Object.entries(raw.region_counts).map(([region, count]) => ({
            region,
            count: safeInt(count),
          }));
        }

        if (!mounted) return;
        setKpis({ totalUsers, totalPredictions, images, audio, real, fake });
        setModelCounts(mc);      // <- canonicalized & merged
        setTimeline(ts);
        setRegions(reg);
      } catch {
        // keep zeros if fetch fails
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const mediaTotal = (kpis.images || 0) + (kpis.audio || 0);

  return (
    <div className="dashboard-page">
      <Header activeLink={activeLink} onNavClick={handleNavClick} />



      {/* ===== Main ===== */}
      <main className="dash-wrap">
        <h1 className="dash-title">SynthGuard Dashboard</h1>

        {/* KPI row – 4 cards */}
        <section className="kpi-grid">
          <KpiCard title="Total Users" value={kpis.totalUsers} />
          <KpiCard title="Total Predictions Made" value={kpis.totalPredictions} />
          <KpiCard title="Images / Audio" value={mediaTotal}>
            <div className="kp-chips">
              <span className="chip">Images: {kpis.images || 0}</span>
              <span className="chip">Audio: {kpis.audio || 0}</span>
            </div>
          </KpiCard>
          <KpiCard title="Labels" value={(kpis.real || 0) + (kpis.fake || 0)}>
            <div className="kp-chips">
              <span className="chip chip-green">Real: {kpis.real || 0}</span>
              <span className="chip chip-blue">Fake: {kpis.fake || 0}</span>
            </div>
          </KpiCard>
        </section>

        {/* Charts */}
        <section className="charts-grid">
          <PieAllModels modelCounts={modelCounts} />
          <BarByCategory modelCounts={modelCounts} />
          <LineActivity timeline={timeline} />
          <RegionCard data={regions} />
        </section>

        {!loading && Object.keys(modelCounts).length === 0 && regions.length === 0 && (
          <p className="muted-note">No chart data yet — upload media and run predictions to see activity.</p>
        )}
      </main>
    </div>
  );
}
